/**
 * Track a selector'd element's viewport rect, polling until it exists.
 *
 * Its own module so SpotlightOverlay.tsx exports components only — a file
 * exporting both defeats Fast Refresh. A hook belongs in its own file anyway,
 * and this one has no callers yet, so keeping it addressable matters more
 * than keeping it adjacent.
 */
import { useEffect, useState } from 'react';

export interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function useTargetPosition(selector?: string): TargetRect | null {
  const [rect, setRect] = useState<TargetRect | null>(null);

  useEffect(() => {
    if (!selector) {
      setRect(null);
      return;
    }

    const update = () => {
      const el = document.querySelector(selector);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };

    let attempts = 0;
    let rafId: number;
    const poll = () => {
      update();
      const el = document.querySelector(selector);
      if (!el && attempts < 180) {
        attempts++;
        rafId = requestAnimationFrame(poll);
      }
    };
    poll();

    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [selector]);

  return rect;
}
